import {IProduct, ISearchRequest} from '../../../models/Search';
import {IStorefrontBundleView} from '../../../models/StorefrontProducts';
import {IStorefrontSuggestedBundle, IStorefrontSuggestedProduct} from '../../../models/Storefront';
import {
    ISuggestionReason,
    suggestionAmountState,
    suggestionDetail,
    suggestionReason,
} from '../suggestedProductsLogic';
import {
    bundleAvailability,
    bundleDetailLine,
    bundleSuggestionReason,
    IBundleAvailability,
} from '../suggestedBundlesLogic';

/**
 * Pure helpers behind the search on the storefront wizard's Add Products and Add Bundles steps.
 *
 * The steps show one list, not two. A suggestion the search happens to match keeps the chip that says why
 * it was put forward; everything else the catalog turned up is the same row without one. Deciding which is
 * which, folding a catalog result into the shape a suggestion row already speaks, and reading "the operator
 * has not asked for anything yet" out of a composed criteria object are all decisions with consequences, so
 * they live here where they can be exercised without mounting Fluent or a redux store.
 */

/** One page of catalog results per search. The wizard is a place to grab a few things, not to browse. */
export const WIZARD_SEARCH_PAGE_SIZE = 25;

/** How long typing sits still before it is searched on. Chip picks apply immediately instead. */
export const WIZARD_SEARCH_DEBOUNCE_MS = 300;

/**
 * The criteria a wizard step starts from.
 *
 * Deliberately not the search page's defaults: `inStock` stays false because the suggestions themselves
 * include out-of-stock products, and a step that offered one and then hid it the moment somebody typed
 * would be lying about what the store can carry.
 */
export const emptyWizardCriteria = (): ISearchRequest => ({
    page: 1,
    pageSize: WIZARD_SEARCH_PAGE_SIZE,
    inStock: false,
    manufacturer: [],
    vendor: [],
    must: [],
    boost: [],
    exclude: [],
    filters: [],
});

const hasText = (value?: string | null): boolean => typeof value === 'string' && value.trim().length > 0;
const hasItems = (value?: unknown[] | null): boolean => Array.isArray(value) && value.length > 0;

/**
 * Whether the composed criteria say anything at all.
 *
 * Emptiness is decided on the fields the omnibox can write and on nothing else, which is why `page`,
 * `pageSize` and the boosted-id list are absent from it: those are always set and would make every step
 * look like it was mid-search. The list mirrors what `buildCriteria` in ProductSearchBox assigns, plus the
 * three list fields its quicksearch rows push on (tags, my tags, product lines), so removing the last chip
 * puts the step back to its suggestions rather than leaving it on an empty result set.
 */
export const wizardCriteriaIsEmpty = (criteria: ISearchRequest): boolean => (
    !hasText(criteria.keywords)
    && !hasItems(criteria.manufacturer)
    && !hasText(criteria.manufacturerPartNumber)
    && !hasText(criteria.category)
    && !hasText(criteria.subCategory)
    && !hasText(criteria.segment)
    && !hasItems(criteria.vendor)
    && criteria.lowPrice === undefined
    && criteria.highPrice === undefined
    && !hasItems(criteria.boost)
    && !hasItems(criteria.must)
    && !hasItems(criteria.exclude)
    && !hasItems(criteria.tags)
    && !hasItems(criteria.userTags)
    && !hasItems(criteria.productLines)
    && !hasItems(criteria.storefrontFilter)
    && criteria.inStock !== true
    && criteria.inStorefront !== true
);

/**
 * One row of the merged list, in the shape the suggestion row already renders.
 *
 * Generic over what the Add button hands back, because both steps add through a path that already exists
 * and takes a suggestion: a catalog result is folded into that shape rather than the add path being widened
 * to know about a second kind of thing.
 */
export interface IWizardSearchRow<T> {
    id: string;
    imageUrl?: string;
    name?: string;
    detail?: string;
    /** Absent on a catalog result. There is no reason to give for something the operator went looking for. */
    reason?: ISuggestionReason;
    availabilityKind: IBundleAvailability['kind'];
    availabilityLabel: string;
    availabilityTooltip?: string;
    cost?: number;
    costFallback?: string;
    /** What the existing add and details paths are handed. Synthesized for a catalog result. */
    addTarget: T;
}

export type IWizardProductRow = IWizardSearchRow<IStorefrontSuggestedProduct>;
export type IWizardBundleRow = IWizardSearchRow<IStorefrontSuggestedBundle>;

/** The two labelled groups of the one list: what we suggested, then what the catalog turned up. */
export interface IWizardSearchGroups<T> {
    suggested: IWizardSearchRow<T>[];
    catalog: IWizardSearchRow<T>[];
}

// --- Products ---------------------------------------------------------------------------------------

/** A suggestion as a row, chip and all. The unsearched list is built from this too, so there is one row. */
export const suggestedProductRow = (product: IStorefrontSuggestedProduct): IWizardProductRow => {
    const costState = suggestionAmountState(product.cost, product.hidePrice);
    return {
        id: product.id,
        imageUrl: product.imageUrl,
        name: product.name,
        detail: suggestionDetail(product),
        reason: suggestionReason(product),
        availabilityKind: product.inStock ? 'inStock' : 'outOfStock',
        availabilityLabel: product.inStock ? 'In stock' : 'Out of stock',
        cost: costState === 'value' ? product.cost : undefined,
        costFallback: costState === 'hidden' ? 'Priced by quote' : 'No cost',
        addTarget: product,
    };
};

/** The cheapest vendor cost on a catalog product, or nothing when no vendor quoted one. Never zero. */
export const catalogProductCost = (product: IProduct): number | undefined => {
    const costs = (product.vendors ?? [])
        .map((vendor) => vendor.cost)
        .filter((cost): cost is number => typeof cost === 'number');
    return costs.length > 0 ? Math.min(...costs) : undefined;
};

/** In stock means at least one vendor says so. A product with no vendors at all is not sellable. */
export const catalogProductInStock = (product: IProduct): boolean =>
    (product.vendors ?? []).some((vendor) => vendor.inStock === true);

/** The primary image, falling back to the first of the gallery. */
export const catalogProductImage = (product: IProduct): string | undefined =>
    product.image || (product.images ?? [])[0]?.url || undefined;

/**
 * A catalog search result, in the shape the add and details paths already take.
 *
 * `source` is left blank on purpose: it is the field that decides the reason chip, and a catalog result has
 * no reason to give. Nothing reads it here, and anything that did would fall through to the neutral wording
 * rather than claiming one of the real sources.
 */
export const asSuggestedProduct = (product: IProduct): IStorefrontSuggestedProduct => ({
    id: product.id,
    name: product.name,
    manufacturer: product.manufacturer,
    mpn: product.manufacturerPartNumber,
    imageUrl: catalogProductImage(product),
    source: '',
    inStock: catalogProductInStock(product),
    cost: catalogProductCost(product),
    hidePrice: product.hidePriceOnStorefront,
    category: product.category,
});

/** A catalog result as a row: everything a suggestion row says apart from why. */
export const catalogProductRow = (product: IProduct): IWizardProductRow => {
    const target = asSuggestedProduct(product);
    const costState = suggestionAmountState(target.cost, target.hidePrice);
    return {
        id: target.id,
        imageUrl: target.imageUrl,
        name: target.name,
        detail: suggestionDetail(target),
        availabilityKind: target.inStock ? 'inStock' : 'outOfStock',
        availabilityLabel: target.inStock ? 'In stock' : 'Out of stock',
        cost: costState === 'value' ? target.cost : undefined,
        costFallback: costState === 'hidden' ? 'Priced by quote' : 'No cost',
        addTarget: target,
    };
};

/**
 * Split one page of search results into the two groups.
 *
 * The results are the whole input: a suggestion that does not match what was typed is not in the list at
 * all, because a "Suggested" group that ignored the query would put the operator's search behind a dozen
 * rows they did not ask for. Which of the results are suggestions is decided by id against the fetched set,
 * and the suggestion object is what renders, so the chip and the reason survive the search. Result order is
 * preserved inside each group: the server has already floated the boosted ids, and re-sorting them here
 * would throw away its ranking.
 */
export const partitionProductResults = (
    results: IProduct[],
    suggestions: IStorefrontSuggestedProduct[]
): IWizardSearchGroups<IStorefrontSuggestedProduct> => {
    const bySuggestionId = new Map(suggestions.filter((s) => !!s?.id).map((s) => [s.id, s]));
    const seen = new Set<string>();
    const suggested: IWizardProductRow[] = [];
    const catalog: IWizardProductRow[] = [];
    (results ?? []).forEach((product) => {
        if (!product?.id || seen.has(product.id)) return;
        seen.add(product.id);
        const match = bySuggestionId.get(product.id);
        if (match) suggested.push(suggestedProductRow(match));
        else catalog.push(catalogProductRow(product));
    });
    return {suggested, catalog};
};

// --- Bundles ----------------------------------------------------------------------------------------

/**
 * The suggested bundles whose name contains what was typed.
 *
 * Client-side because the whole suggested set is already in hand and a second round trip to filter a dozen
 * names would only make the list arrive later. An empty term filters nothing.
 */
export const filterSuggestedBundles = (
    bundles: IStorefrontSuggestedBundle[],
    term: string
): IStorefrontSuggestedBundle[] => {
    const needle = (term ?? '').trim().toLowerCase();
    if (needle.length === 0) return bundles ?? [];
    return (bundles ?? []).filter((bundle) => (bundle.name ?? '').toLowerCase().includes(needle));
};

/** A suggested bundle as a row, chip and all. */
export const suggestedBundleRow = (bundle: IStorefrontSuggestedBundle): IWizardBundleRow => {
    const availability = bundleAvailability(bundle);
    // A bundle is never priced by quote the way a product can be, so there is no hidden state here.
    const costState = suggestionAmountState(bundle.cost, undefined);
    return {
        id: bundle.id,
        imageUrl: bundle.imageUrl,
        name: bundle.name,
        detail: bundleDetailLine(bundle),
        reason: bundleSuggestionReason(bundle),
        availabilityKind: availability.kind,
        availabilityLabel: availability.label,
        availabilityTooltip: availability.tooltip,
        cost: costState === 'value' ? bundle.cost : undefined,
        costFallback: 'No cost',
        addTarget: bundle,
    };
};

/** What the admin view says when it could not price a bundle. It does not carry the API's own reason. */
export const BUNDLE_NOT_PRICED = 'This storefront could not put a price on this bundle.';

/**
 * An admin bundle view in the shape the add path already takes.
 *
 * The admin view carries neither a stock flag nor an unpriced reason, so both are inferred: a missing price
 * is the one honest reading of "this store cannot sell it", and a max quantity the store has resolved to
 * zero is out of stock. Anything the view does not say is left unsaid rather than guessed at.
 */
export const asSuggestedBundle = (view: IStorefrontBundleView): IStorefrontSuggestedBundle => {
    const priced = view.price !== undefined && view.price !== null;
    return {
        id: view.id,
        name: view.name,
        productCount: (view.products ?? []).length,
        imageUrl: view.image,
        source: '',
        cost: view.cost?.cost,
        price: view.price,
        inStock: view.maxQuantity === undefined || view.maxQuantity === null || view.maxQuantity > 0,
        unpricedReason: priced ? undefined : BUNDLE_NOT_PRICED,
    };
};

/** A catalog bundle as a row: everything a suggested bundle says apart from why. */
export const catalogBundleRow = (view: IStorefrontBundleView): IWizardBundleRow => {
    const bundle = asSuggestedBundle(view);
    const availability = bundleAvailability(bundle);
    const costState = suggestionAmountState(bundle.cost, undefined);
    return {
        id: bundle.id,
        imageUrl: bundle.imageUrl,
        name: bundle.name,
        detail: bundleDetailLine(bundle),
        availabilityKind: availability.kind,
        availabilityLabel: availability.label,
        availabilityTooltip: availability.tooltip,
        cost: costState === 'value' ? bundle.cost : undefined,
        costFallback: 'No cost',
        addTarget: bundle,
    };
};

/**
 * The bundle twin of partitionProductResults, with one difference that matters.
 *
 * The suggested group here is handed in already filtered rather than read out of the results: the bundle
 * search has no boosting to lean on, so the two groups come from two places and the catalog one has to have
 * the suggestions taken out of it or every matching bundle would be listed twice.
 */
export const partitionBundleResults = (
    results: IStorefrontBundleView[],
    suggested: IStorefrontSuggestedBundle[]
): IWizardSearchGroups<IStorefrontSuggestedBundle> => {
    const suggestedIds = new Set((suggested ?? []).map((bundle) => bundle.id));
    const seen = new Set<string>();
    const catalog: IWizardBundleRow[] = [];
    (results ?? []).forEach((view) => {
        if (!view?.id || suggestedIds.has(view.id) || seen.has(view.id)) return;
        seen.add(view.id);
        catalog.push(catalogBundleRow(view));
    });
    return {suggested: (suggested ?? []).map(suggestedBundleRow), catalog};
};
